import { IInteractiveVoiceResponses } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions, Equal } from '@llws/dynamic-router-utils';
import { isEqual } from 'lodash-es';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrictV2 } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IEditableHours } from '@/shared/interfaces/property-ivr-details';

// Base store configuration
const adminGatewayResourcePath: string = '/call-tracking-api/interactive_voice_responses';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  alias: 'ivr',
  relations: [
    { path: 'ivr.buildingsHasInteractiveVoiceResponses', alias: 'bhivr' },
    { path: 'ivr.callGroupsHasInteractiveVoiceResponses', alias: 'cghivr' },
  ],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IInteractiveVoiceResponses>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

/**
 * Recompacts child IVR route digits before persisting, grouped per parent IVR (tab):
 * - Enabled routes are assigned sequential digits starting at 1 (sorted by current digit, null digits go last)
 * - Disabled routes are assigned null
 * Operates independently per parent so each tab's digits start at 1 and don't bleed into other tabs.
 * Commits changes directly so they are included in the subsequent save payload.
 */
const recompactRoutingDigits = (
  mergedIVRs: IInteractiveVoiceResponses[],
  commit: (mutation: string, payload: unknown) => void,
): void => {
  const childIVRs = mergedIVRs.filter((ivr) => ivr.parentInteractiveVoiceResponseId !== null);

  // Group by parent so each tab's routes are recompacted independently
  const byParent = new Map<number, IInteractiveVoiceResponses[]>();
  childIVRs.forEach((ivr) => {
    const parentId = ivr.parentInteractiveVoiceResponseId as number;
    if (!byParent.has(parentId)) byParent.set(parentId, []);
    byParent.get(parentId)!.push(ivr);
  });

  byParent.forEach((routes) => {
    const enabled = routes.filter((r) => r.status === 'enabled').sort((a, b) => {
      if (a.actionSelectDigit === null) return 1;
      if (b.actionSelectDigit === null) return -1;
      return (a.actionSelectDigit ?? 0) - (b.actionSelectDigit ?? 0);
    });
    const disabled = routes.filter((r) => r.status === 'disabled');
    enabled.forEach((r, index) => commit('setIVREdited', { id: r.id ?? 0, key: 'actionSelectDigit', value: index + 1 }));
    disabled.forEach((r) => commit('setIVREdited', { id: r.id ?? 0, key: 'actionSelectDigit', value: null }));
  });
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    editableHours: {} as Record<string, IEditableHours[]>,
  },

  getters: {
    ...base.getters,

    /**
     * Returns the full original IVRs list with local edits applied on top of it.
     * Primary getter for UI state, always use this for UI you want reflecting user changes.
     * NEVER write or mutate the objects returned by this getter directly.
     * To update values, always use the 'setIVREdited' mutation.
     */
    mergedIVRs(state: IStateStrictV2<IInteractiveVoiceResponses>): IInteractiveVoiceResponses[] {
      const data = state.list?.data || [];

      if (Object.keys(state.changes).length === 0) return data;

      return data.map((ivr) => {
        const changesForId = state.changes[ivr.id ?? 0];
        return changesForId ? { ...ivr, ...changesForId } : ivr;
      });
    },

    /** Returns root IVRs (e.g., On Hours / Off Hours) without a parent IVR. Does NOT include local UI edits */
    rootIVRs(state: IStateStrictV2<IInteractiveVoiceResponses>): IInteractiveVoiceResponses[] {
      return (state.list?.data?.filter((ivr) => ivr?.parentInteractiveVoiceResponseId === null) || []);
    },

    /** Returns children IVRs (e.g., Leasing / Marketing) with a parent IVR. Does NOT include local UI edits */
    childrenIVRs(state: IStateStrictV2<IInteractiveVoiceResponses>): IInteractiveVoiceResponses[] {
      return (state.list?.data?.filter((ivr) => ivr?.parentInteractiveVoiceResponseId !== null) || []);
    },

    /** Returns only the IVRs that were edited on the UI */
    editedIVRs(state: IStateStrictV2<IInteractiveVoiceResponses>): IInteractiveVoiceResponses[] {
      if (!state.list?.data) return [];

      return Object.entries(state.changes).map(([id, changes]) => {
        const original = (state.list?.data ?? []).find((v) => v.id === Number(id));
        return {
          ...original,
          ...changes,
        } as IInteractiveVoiceResponses;
      });
    },

    /** Returns editable hours state for all tabs */
    editableHours(state: IStateStrictV2<IInteractiveVoiceResponses> & { editableHours: Record<string, IEditableHours[]> }): Record<string, IEditableHours[]> {
      return state.editableHours || {};
    },

  },

  mutations: {
    ...base.mutations,

    setEditableHours(state: IStateStrictV2<IInteractiveVoiceResponses>
    & { editableHours: Record<string, IEditableHours[]> }, payload: { id: string; hours: IEditableHours[] }) {
      state.editableHours = {
        ...state.editableHours,
        [payload.id]: payload.hours,
      };
    },

    /** Enhanced setEdited that supports list items by passing an ID */
    setIVREdited(
      state: IStateStrictV2<IInteractiveVoiceResponses>,
      payload: { id: number; key: keyof IInteractiveVoiceResponses; value: any },
    ) {
      const { id, key, value } = payload;

      // Find the original item in the list to check if we're reverting to original value
      const originalItem = state.list?.data?.find((ivr) => ivr.id === id);
      const originalValue = originalItem ? originalItem[key] : undefined;

      const isSameAsOriginal = isEqual(value, originalValue);
      const updatedChangesForId = { ...(state.changes[id] || {}) };

      if (isSameAsOriginal) {
        delete updatedChangesForId[key];
      } else {
        updatedChangesForId[key] = value;
      }

      // Update the changes object reactively
      if (Object.keys(updatedChangesForId).length === 0) {
        const allChanges = { ...state.changes };
        delete allChanges[id];
        state.changes = allChanges;
      } else {
        state.changes = {
          ...state.changes,
          [id]: updatedChangesForId,
        };
      }
    },
  },

  actions: {
    ...base.actions,

    /** Fetches all IVR settings for a specific building. */
    async getIVRSettings(ctx: IAction<IStateStrictV2<IInteractiveVoiceResponses>>, buildingId: string) {
      await base.actions.get(ctx as any, {
        filters: {
          where: [Equal('bhivr.buildingId', buildingId)],
        },
      });
    },

    /** Saves all modified IVR records and merges the updated data back into the list state. */
    async saveEditedIVRSettings(ctx: IAction<IStateStrictV2<IInteractiveVoiceResponses>>) {
      const { getters, commit, state } = ctx;

      recompactRoutingDigits(getters.mergedIVRs, commit);

      const { editedIVRs, list } = getters;

      if (!editedIVRs?.length) return;

      // Separate IVR fields from join table updates
      const callGroupRelationsToUpdate: Array<{ id: number; callGroupId: number; interactiveVoiceResponseId: number }> = [];
      const callGroupRelationsToCreate: Array<{ callGroupId: number; interactiveVoiceResponseId: number }> = [];

      const data = editedIVRs.map((ivr: IInteractiveVoiceResponses) => {
        const { buildingsHasInteractiveVoiceResponses, callGroupsHasInteractiveVoiceResponses, ...rest } = ivr;

        const changes = state.changes[ivr.id ?? 0];

        // Collect call group relations that were edited for separate API call
        if (changes?.callGroupsHasInteractiveVoiceResponses !== undefined && callGroupsHasInteractiveVoiceResponses) {
          const originalIVR = list.find((item: IInteractiveVoiceResponses) => item.id === ivr.id);
          const existingRelation = originalIVR?.callGroupsHasInteractiveVoiceResponses?.[0];

          callGroupsHasInteractiveVoiceResponses.forEach((relation) => {
            // If original had a relation, update it with the existing ID
            if (existingRelation?.id) {
              callGroupRelationsToUpdate.push({ ...relation, id: existingRelation.id });
            } else {
              // Otherwise create a new relation
              callGroupRelationsToCreate.push(relation);
            }
          });
        }

        return rest;
      });

      // Save IVR fields (excluding join tables)
      await adminRequest.put(adminGatewayResourcePath, data);

      // Save call group relations to join table
      if (callGroupRelationsToUpdate.length > 0) {
        await adminRequest.put('/call-tracking-api/call_groups_has_interactive_voice_responses', callGroupRelationsToUpdate);
      }
      if (callGroupRelationsToCreate.length > 0) {
        await adminRequest.post('/call-tracking-api/call_groups_has_interactive_voice_responses', callGroupRelationsToCreate);
      }

      // Re-fetch to get updated join table data
      const buildingId = list[0]?.buildingsHasInteractiveVoiceResponses?.[0]?.buildingId;
      if (buildingId) {
        await base.actions.get(ctx, {
          filters: {
            where: [Equal('bhivr.buildingId', buildingId)],
          },
        });
      }

      commit('resetChanges');
    },
  },
};
